import React, {useCallback, useEffect} from 'react';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import PageTitle from '../shared/PageTitle';
import {createCoach, deleteCoach, getTeam} from '../../api/adminAPI';
import {Link as RouterLink, useParams} from 'react-router-dom';
import {formatDateOnly} from '../../services/dateService';
import {
  Box,
  Button,
  FormGroup,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import BackButton from '../shared/BackButton';
import {Coach, Player, Team} from '../../interfaces';
import styles from '../../styles';
import UpdateTeamModal from './UpdateTeamModal';
import PageSubtitle from '../shared/PageSubtitle';
import {updatePlayer} from '../../api/playerAPI';
import {removeDevice} from '../../api/deviceAPI';
import {useForm, Control, FieldValues} from 'react-hook-form';
import FormTextField from '../shared/FormTextField';

interface Props {}

type FormValues = {
  email: string;
};

const AdminTeamDetail: React.FC<Props> = () => {
  const {t} = useTranslation();
  const {teamId} = useParams();

  const [team, setTeam] = React.useState<Team | null>(null);
  const [pending, setPending] = React.useState(false);
  const [showAddCoachDialog, setShowAddCoachDialog] = React.useState(false);
  const {handleSubmit, reset, control} = useForm<FormValues>({
    defaultValues: {
      email: '',
    },
  });

  const fetchData = useCallback(async () => {
    setPending(true);
    const team = await getTeam(parseInt(teamId ?? ''));
    setTeam(team);
    setPending(false);
  }, [teamId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddCoachClick = useCallback(() => {
    setShowAddCoachDialog(true);
  }, []);

  const handleAddCoachClose = useCallback(() => {
    setShowAddCoachDialog(false);
    reset();
  }, [reset]);

  const handleAddCoachSubmit = useCallback(
    async (data: FormValues) => {
      setPending(true);
      await createCoach({
        email: data.email,
        teamId: parseInt(teamId ?? ''),
      });
      handleAddCoachClose();
      fetchData();
    },
    [fetchData, handleAddCoachClose, teamId],
  );

  const handleDeleteCoach = useCallback(
    async (coach: Coach) => {
      setPending(true);
      // ask confirmation dialog
      const res = confirm(
        `${t('admin.confirmDeleteCoach')}\n${coach.profile?.email}`,
      );
      if (res) {
        // delete coach
        await deleteCoach({
          id: coach.id!,
        });
        fetchData();
      } else {
        setPending(false);
      }
    },
    [fetchData, t],
  );

  const handleRemovePlayer = useCallback(
    async (player: Player) => {
      // ask confirmation dialog
      const res = confirm(
        `${t('admin.confirmDeletePlayer')}\n${player.profile?.firstName} ${player.profile?.lastName}`,
      );
      if (res) {
        // delete player
        await updatePlayer({
          id: player.id as number,
          isActive: false,
        });
        fetchData();
      } else {
        setPending(false);
      }
    },
    [fetchData, t],
  );

  const handleRemovePlayerAndUnpairDevice = useCallback(
    async (player: Player) => {
      // ask confirmation dialog
      const res = confirm(
        `${t('admin.confirmDeletePlayer')}\n${player.profile?.firstName} ${player.profile?.lastName} ${t('admin.andUnpairDevices')}`,
      );
      if (res) {
        // unpair all devices for this player
        // delete player
        for (const device of player?.profile?.devices ?? []) {
          await removeDevice({
            deviceId: device.id as number,
            profileId: player.profile?.id,
          });
        }
        await updatePlayer({
          id: player.id as number,
          isActive: false,
        });
        fetchData();
      } else {
        setPending(false);
      }
    },
    [fetchData, t],
  );
  return (
    <>
      <Helmet>
        <title>
          {t('navigation.pageTitle', {
            pageName: t('pages.teamDetail', {teamName: team?.name}),
          })}
        </title>
      </Helmet>
      <Box>
        <PageTitle>{t('pages.teamDetail', {teamName: team?.name})}</PageTitle>
        <BackButton navigationUrl="/admin/teams">{t('pages.teams')}</BackButton>
        <Typography>
          {t('admin.code')}: {team?.code ?? '—'}
        </Typography>
        <Typography>
          {t('admin.teamSessionGroupsEnabled')}:{' '}
          {team?.teamSessionGroupsEnabled ? t('general.yes') : t('general.no')}
        </Typography>
        <Typography>
          {t('admin.isPro')}: {team?.isPro ? t('general.yes') : t('general.no')}
        </Typography>
        <Typography>
          {t('admin.hideLeaderboard')}:{' '}
          {team?.hideLeaderboard ? t('general.yes') : t('general.no')}
        </Typography>
        <Typography sx={{mb: 2}}>
          {t('admin.membershipEndDate')}:{' '}
          {formatDateOnly(team?.membershipTrialEndDate) || '—'}
        </Typography>
        {team ? (
          <UpdateTeamModal team={team} loading={pending} onUpdate={fetchData} />
        ) : (
          <Button sx={{mb: 2, mr: 2}} disabled={true}>
            {t('admin.updateTeam')}
          </Button>
        )}
        <Button
          sx={{mb: 2, mr: 2}}
          disabled={pending}
          onClick={handleAddCoachClick}>
          {t('admin.addCoach')}
        </Button>
        <Button
          sx={{mb: 2, mr: 2}}
          variant="outlined"
          component={RouterLink}
          to={`/teams/${team?.id}/teamsessions`}
          disabled={pending || !team}>
          {t('pages.teamSessions')}
        </Button>
        <Modal
          open={showAddCoachDialog}
          onClose={handleAddCoachClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Box sx={styles.modal}>
            <Typography variant="h4" sx={{mb: 2}}>
              {t('admin.addCoach')}
            </Typography>
            <form onSubmit={handleSubmit(handleAddCoachSubmit)}>
              <FormGroup>
                <FormTextField
                  control={control as unknown as Control<FieldValues>}
                  name="email"
                  label={t('general.email') as string}
                  required
                />
                <Button type="submit" disabled={pending}>
                  {t('general.submit')}
                </Button>
              </FormGroup>
            </form>
          </Box>
        </Modal>
        <TableContainer sx={{mb: 5}} component={Paper}>
          <Table aria-label={t('ariaLabels.tables.adminTeamDetail') as string}>
            <TableHead>
              <TableRow>
                <TableCell>{t('general.name')}</TableCell>
                <TableCell>{t('general.email')}</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...(team?.coaches ?? [])].map((coach: Coach) => (
                <TableRow key={coach.id}>
                  <TableCell>{`${coach.profile?.firstName} ${coach.profile?.lastName}`}</TableCell>
                  <TableCell>{coach.profile?.email}</TableCell>
                  <TableCell align="right">
                    <Button
                      onClick={() => handleDeleteCoach(coach)}
                      disabled={pending}>
                      {t('general.remove')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <PageSubtitle>{t('general.players')}</PageSubtitle>
        <TableContainer sx={{mb: 5}} component={Paper}>
          <Table aria-label={t('ariaLabels.tables.adminTeamDetail') as string}>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.profileId')}</TableCell>
                <TableCell>{t('general.name')}</TableCell>
                <TableCell>{t('general.email')}</TableCell>
                <TableCell>{t('admin.isActive')}</TableCell>
                <TableCell align="right"></TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...(team?.players ?? [])]
                // sort by isActive true first then by last name
                .sort((a, b) => {
                  // First sort by isActive
                  if (a.isActive !== b.isActive) {
                    return a.isActive ? -1 : 1;
                  }
                  // Then sort by lastName
                  return (a.profile?.lastName ?? '').localeCompare(
                    b.profile?.lastName ?? '',
                  );
                })
                .map(player => (
                  <TableRow key={player.id}>
                    <TableCell>{player.profile?.id}</TableCell>
                    <TableCell>{`${player.profile?.firstName} ${player.profile?.lastName}`}</TableCell>
                    <TableCell>{player.profile?.email}</TableCell>
                    <TableCell>
                      {player.isActive ? t('general.yes') : t('general.no')}
                    </TableCell>
                    <TableCell align="right">
                      {player.isActive && (
                        <Button
                          onClick={() => handleRemovePlayer(player)}
                          disabled={pending}>
                          {t('admin.removePlayerOnly')}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {player.isActive && (
                        <Button
                          onClick={() =>
                            handleRemovePlayerAndUnpairDevice(player)
                          }
                          disabled={pending}>
                          {t('admin.removePlayerAndUnpairDevice')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default React.memo(AdminTeamDetail);
